import {
  Grid,
  Stack,
  Badge,
  Group,
  Box,
  Paper,
} from '@mantine/core'
import { useState, useEffect } from 'react'
import { useEtlActivity, MAX_TIMELINE_ENTRIES, historyCaption } from '../hooks/useEtlActivity'
import { isConnected, onConnectionStateChange } from '../services/signalr'
import { PageTitle, SectionTitle, HairlineDivider } from '@lighthouse/design'
import {
  ActiveJobBanner,
  LiveStatusQuiet,
  LiveStatusUnavailable,
  StaleWorkBanner,
} from '../components/etl-activity/ActiveJobBanner'
import { ModuleCard } from '../components/etl-activity/ModuleCard'
import { LiveTimeline } from '../components/etl-activity/LiveTimeline'
import { JobHistory } from '../components/etl-activity/JobHistory'

/**
 * Live Activity. Whether anything is running is the SERVER's answer (api/observatory/operations/live,
 * with the worker's own stale rule); the hub connection only adds detail to it and asks again sooner.
 * So a dropped connection or a missed completion event cannot leave the banner saying "running".
 */
export function EtlActivityPage() {
  const {
    live,
    running,
    timeline,
    history,
    historyTotal,
    historyLoading,
    historyError,
  } = useEtlActivity()

  const [connected, setConnected] = useState(isConnected)
  useEffect(() => onConnectionStateChange(setConnected), [])

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <PageTitle subtitle="Real-time monitoring of ETL pipeline operations">ETL Activity</PageTitle>
        <Badge
          color={connected ? 'green' : 'red'}
          variant="light"
          size="lg"
          mt={4}
          title="The live event stream. What is running is read from the server either way."
        >
          {connected ? 'Connected' : 'Disconnected'}
        </Badge>
      </Group>

      {/* What is in flight, as served */}
      {live.kind === 'running' && running && <ActiveJobBanner view={running} />}
      {live.kind === 'stale' && <StaleWorkBanner status={live} />}
      {live.kind === 'unavailable' && <LiveStatusUnavailable error={live.error} />}
      {live.kind === 'loading' && <LiveStatusQuiet state="loading" />}
      {live.kind === 'idle' && <LiveStatusQuiet state="idle" observedAt={live.observedAt} />}

      {/* One card per served run with a live heartbeat */}
      {running && running.runs.length > 0 && (
        <>
          <SectionTitle subtitle="Runs with a live heartbeat, as the worker recorded them">Active Runs</SectionTitle>
          <Grid>
            {running.runs.map((run) => (
              <Grid.Col key={run.executionId} span={{ base: 12, sm: 6, lg: 4 }}>
                <ModuleCard run={run} />
              </Grid.Col>
            ))}
          </Grid>
        </>
      )}

      <HairlineDivider />

      {/* Live Timeline */}
      <Paper
        shadow="sm"
        p="lg"
        radius="md"
        className="surface-card"
      >
        <SectionTitle subtitle={`Real-time ETL events from this session — the latest ${MAX_TIMELINE_ENTRIES} are kept`}>
          Live Event Stream
        </SectionTitle>
        <Box mt="md">
          <LiveTimeline entries={timeline} maxHeight={400} />
        </Box>
      </Paper>

      <HairlineDivider />

      {/* Job History */}
      <Paper
        shadow="sm"
        p="lg"
        radius="md"
        className="surface-card"
      >
        {/* One page of the log: the caption says how much of it, from the served total. */}
        <SectionTitle subtitle={historyCaption(history?.length ?? null, historyTotal)}>Job History</SectionTitle>
        <Box mt="md">
          <JobHistory activities={history} isLoading={historyLoading} error={historyError} />
        </Box>
      </Paper>
    </Stack>
  )
}
